/**
 * No. 4 High Terrace — booking widget entry point.
 *
 * Mounts itself on DOMContentLoaded into:
 *   #no4-booking-widget                          (canonical id)
 *   any element with [data-no4-widget]            (additional mounts)
 *
 * Reads its config from data attributes on the mount element:
 *   data-supabase-url
 *   data-anon-key
 *   data-stripe-key
 *   data-privacy-url (optional)
 *
 * Exposes a small global (window.No4Widget) for manual mounting:
 *   window.No4Widget.mount(element, { supabaseUrl, anonKey, stripeKey })
 */

import './styles.css';

import { BookingAPI, type CreateBookingResponse } from './api.js';
import { CalendarStep, type CalendarState } from './steps/Calendar.js';
import { GuestFormStep, type GuestFormState } from './steps/GuestForm.js';
import { PaymentStep } from './steps/PaymentForm.js';
import { ConfirmationStep } from './steps/Confirmation.js';
import { APIError } from './api.js';
import { escapeHTML } from './utils/formatting.js';
import { toISODate } from './utils/dates.js';

export interface WidgetConfig {
  supabaseUrl: string;
  anonKey: string;
  stripeKey: string;
  privacyUrl?: string;
}

type Flow = 'booking' | 'enquiry';
type StepName = 'calendar' | 'guest' | 'payment' | 'confirmation';

class WidgetInstance {
  private readonly root: HTMLElement;
  private readonly config: WidgetConfig;
  private readonly api: BookingAPI;

  private containerEl: HTMLDivElement | null = null;
  private paymentOverlayEl: HTMLDivElement | null = null;
  private bodyEl: HTMLDivElement | null = null;
  private footerEl: HTMLDivElement | null = null;
  private stepsEl: HTMLUListElement | null = null;
  private titleEl: HTMLHeadingElement | null = null;
  private flowToggleEl: HTMLButtonElement | null = null;

  private currentStep: StepName = 'calendar';
  private flow: Flow = 'booking';

  private calendar: CalendarStep | null = null;
  private guestForm: GuestFormStep | null = null;
  private payment: PaymentStep | null = null;
  private confirmation: ConfirmationStep | null = null;

  private calendarState: CalendarState = {
    checkIn: null,
    checkOut: null,
    numAdults: 2,
    numChildren: 0,
    childAges: [],
    hasPets: false,
    serverPrice: null,
  };
  private guestState: GuestFormState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
    gdprConsent: false,
  };
  private bookingResult: CreateBookingResponse | null = null;

  private calendarValid = false;
  private guestValid = false;
  private enquirySubmitting = false;
  private enquiryError: string | null = null;
  /**
   * Populated when the server returns 409 (dates just got taken) from a
   * payment attempt. Displayed above the calendar on the next render so
   * the guest understands why they were bounced back.
   */
  private datesUnavailableMessage: string | null = null;

  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(root: HTMLElement, config: WidgetConfig) {
    this.root = root;
    this.config = config;
    this.api = new BookingAPI(config.supabaseUrl, config.anonKey);
    this.mountInline();
    this.renderCurrentStep();
  }

  /* ----------------------------------------------------------------- */
  /*  Inline container                                                  */
  /* ----------------------------------------------------------------- */

  private mountInline(): void {
    this.root.classList.add('no4w-root');
    this.root.innerHTML = `
      <div class="no4w-inline">
        <div class="no4w-inline-topbar">
          <h2 class="no4w-modal-title" data-role="title">Book your stay</h2>
          <button type="button" class="no4w-flow-toggle" data-action="toggle-flow"></button>
        </div>
        <ul class="no4w-steps" data-role="steps" aria-hidden="true"></ul>
        <div data-role="step-host"></div>
        <div class="no4w-inline-footer" data-role="footer"></div>
      </div>
    `;
    this.containerEl = this.root.querySelector<HTMLDivElement>('.no4w-inline');
    this.bindInlineRefs();
    this.flowToggleEl?.addEventListener('click', () => this.toggleFlow());
    this.updateFlowToggleLabel();
  }

  private bindInlineRefs(): void {
    if (!this.containerEl) return;
    this.bodyEl = this.containerEl.querySelector<HTMLDivElement>('[data-role="step-host"]');
    this.footerEl = this.containerEl.querySelector<HTMLDivElement>('[data-role="footer"]');
    this.stepsEl = this.containerEl.querySelector<HTMLUListElement>('[data-role="steps"]');
    this.titleEl = this.containerEl.querySelector<HTMLHeadingElement>('[data-role="title"]');
    this.flowToggleEl = this.containerEl.querySelector<HTMLButtonElement>(
      '[data-action="toggle-flow"]',
    );
  }

  private toggleFlow(): void {
    this.flow = this.flow === 'booking' ? 'enquiry' : 'booking';
    this.updateFlowToggleLabel();
    this.updateTitle();
    this.updateSteps();
    // Re-render the current step so its behaviour (validation, price fetching)
    // reflects the new flow. renderCurrentStep tears down the old step
    // instance (destroying any in-flight fetches) before instantiating the new
    // one. Guest form data is preserved via this.guestState; calendar
    // selection may reset — acceptable trade-off.
    this.renderCurrentStep();
  }

  private updateFlowToggleLabel(): void {
    if (!this.flowToggleEl) return;
    this.flowToggleEl.textContent =
      this.flow === 'booking'
        ? 'Just want to enquire? Send an enquiry instead'
        : 'Prefer to book online now? Switch to booking';
  }

  private restart(): void {
    this.calendarState = {
      checkIn: null,
      checkOut: null,
      numAdults: 2,
      numChildren: 0,
      childAges: [],
      hasPets: false,
      serverPrice: null,
    };
    this.guestState = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      notes: '',
      gdprConsent: false,
    };
    this.bookingResult = null;
    this.calendarValid = false;
    this.guestValid = false;
    this.enquirySubmitting = false;
    this.enquiryError = null;
    this.flow = 'booking';
    this.currentStep = 'calendar';
    this.updateFlowToggleLabel();
    this.renderCurrentStep();
  }

  /* ----------------------------------------------------------------- */
  /*  Payment overlay lifecycle                                         */
  /* ----------------------------------------------------------------- */

  private openPaymentOverlay(): void {
    if (this.paymentOverlayEl) return;
    const titleId = `no4w-payment-title-${Math.random().toString(36).slice(2, 9)}`;
    const overlay = document.createElement('div');
    overlay.className = 'no4w-modal-overlay no4w-root';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', titleId);
    overlay.innerHTML = `
      <div class="no4w-modal" role="document">
        <header class="no4w-modal-header">
          <h2 class="no4w-modal-title" data-role="title" id="${titleId}">Payment</h2>
          <button type="button" class="no4w-modal-close" aria-label="Back" data-action="back-from-payment">×</button>
        </header>
        <div class="no4w-modal-body">
          <ul class="no4w-steps" data-role="steps" aria-hidden="true"></ul>
          <div data-role="step-host"></div>
        </div>
        <div class="no4w-modal-footer" data-role="footer"></div>
      </div>
    `;
    document.body.appendChild(overlay);
    this.paymentOverlayEl = overlay;
    // Point step-render refs at overlay while payment is open.
    this.bodyEl = overlay.querySelector<HTMLDivElement>('[data-role="step-host"]');
    this.footerEl = overlay.querySelector<HTMLDivElement>('[data-role="footer"]');
    this.stepsEl = overlay.querySelector<HTMLUListElement>('[data-role="steps"]');
    this.titleEl = overlay.querySelector<HTMLHeadingElement>('[data-role="title"]');

    overlay
      .querySelector<HTMLButtonElement>('[data-action="back-from-payment"]')
      ?.addEventListener('click', () => {
        if (this.payment?.isSubmitting()) return;
        this.currentStep = 'guest';
        this.renderCurrentStep();
      });

    // Initial focus on the close button so keyboard users have a clear
    // starting point inside the modal.
    overlay.querySelector<HTMLButtonElement>('.no4w-modal-close')?.focus();

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !this.payment?.isSubmitting()) {
        this.currentStep = 'guest';
        this.renderCurrentStep();
        return;
      }
      if (e.key === 'Tab') {
        this.trapFocus(overlay, e);
      }
    };
    document.addEventListener('keydown', this.keydownHandler);
    document.body.style.overflow = 'hidden';
  }

  /**
   * Basic focus trap: on Tab / Shift+Tab, cycle focus among focusable
   * elements within the overlay. Ignores Stripe iframes — the browser
   * handles cross-frame Tab natively so we only wrap when focus is on
   * the first or last focusable in our own DOM.
   */
  private trapFocus(overlay: HTMLElement, e: KeyboardEvent): void {
    const focusables = Array.from(
      overlay.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !overlay.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  private closePaymentOverlay(): void {
    if (!this.paymentOverlayEl) return;
    this.paymentOverlayEl.remove();
    this.paymentOverlayEl = null;
    document.body.style.overflow = '';
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    // Restore inline refs.
    this.bindInlineRefs();
  }

  /* ----------------------------------------------------------------- */
  /*  Steps                                                             */
  /* ----------------------------------------------------------------- */

  private allStepNames(): StepName[] {
    if (this.flow === 'enquiry') return ['calendar', 'guest', 'confirmation'];
    return ['calendar', 'guest', 'payment', 'confirmation'];
  }

  private renderCurrentStep(): void {
    // Enter/leave payment overlay as needed based on target step.
    if (this.currentStep === 'payment' && !this.paymentOverlayEl) {
      this.openPaymentOverlay();
    } else if (this.currentStep !== 'payment' && this.paymentOverlayEl) {
      this.closePaymentOverlay();
    }

    if (!this.bodyEl || !this.footerEl) return;

    // tear down previous step instances
    this.calendar?.destroy();
    this.guestForm?.destroy();
    this.payment?.destroy();
    this.confirmation?.destroy();
    this.calendar = null;
    this.guestForm = null;
    this.payment = null;
    this.confirmation = null;

    this.bodyEl.innerHTML = '<div data-role="step-mount"></div>';
    const mount = this.bodyEl.querySelector<HTMLDivElement>(
      '[data-role="step-mount"]',
    );
    if (!mount) return;

    this.updateTitle();
    this.updateSteps();

    switch (this.currentStep) {
      case 'calendar':
        this.calendarValid = false;
        // Show the "dates just got taken" banner above the calendar if we
        // were just bounced back from the payment step. Clear after render.
        if (this.datesUnavailableMessage) {
          const banner = document.createElement('div');
          banner.className = 'no4w-banner no4w-banner--warn';
          banner.setAttribute('role', 'status');
          banner.textContent = this.datesUnavailableMessage + ' Please pick a new date range.';
          mount.appendChild(banner);
          this.datesUnavailableMessage = null;
        }
        this.calendar = new CalendarStep(mount, {
          api: this.api,
          flow: this.flow,
          initial: this.calendarState,
          onValidityChange: (v) => {
            this.calendarValid = v;
            this.renderFooter();
          },
          onStateChange: (s) => {
            this.calendarState = s;
          },
        });
        break;

      case 'guest':
        this.guestValid = false;
        this.guestForm = new GuestFormStep(mount, {
          initial: this.guestState,
          privacyUrl: this.config.privacyUrl,
          onValidityChange: (v) => {
            this.guestValid = v;
            this.renderFooter();
          },
          onStateChange: (s) => {
            this.guestState = s;
          },
        });
        break;

      case 'payment':
        this.payment = new PaymentStep(mount, {
          api: this.api,
          stripePublishableKey: this.config.stripeKey,
          calendar: this.calendarState,
          guest: this.guestState,
          childAges: this.calendarState.childAges,
          hasPets: this.calendarState.hasPets,
          onSuccess: (result) => {
            this.bookingResult = result;
            this.currentStep = 'confirmation';
            this.renderCurrentStep();
          },
          onCancel: () => {
            this.currentStep = 'guest';
            this.renderCurrentStep();
          },
          onDatesUnavailable: (message) => {
            // Server reported the dates were taken. Clear the calendar
            // selection so the guest is forced to pick fresh dates, then
            // bounce back to the calendar step with the server's message
            // surfaced as the calendar's own error.
            this.calendarState.checkIn = null;
            this.calendarState.checkOut = null;
            this.calendarState.serverPrice = null;
            this.datesUnavailableMessage = message;
            this.currentStep = 'calendar';
            this.renderCurrentStep();
          },
        });
        break;

      case 'confirmation':
        if (!this.bookingResult) {
          mount.innerHTML = `<div class="no4w-banner no4w-banner--error">
            Missing booking result. Please contact us.
          </div>`;
        } else {
          this.confirmation = new ConfirmationStep(mount, {
            flow: this.flow,
            result: this.bookingResult,
            calendar: this.calendarState,
            guest: this.guestState,
          });
        }
        break;
    }

    this.renderFooter();
  }

  private updateTitle(): void {
    if (!this.titleEl) return;
    const map: Record<StepName, { booking: string; enquiry: string }> = {
      calendar: { booking: 'Choose your dates', enquiry: 'Tell us your dates' },
      guest: { booking: 'Your details', enquiry: 'Your details' },
      payment: { booking: 'Payment', enquiry: 'Payment' },
      confirmation: { booking: 'Booking confirmed', enquiry: 'Enquiry sent' },
    };
    this.titleEl.textContent = map[this.currentStep][this.flow];
  }

  private updateSteps(): void {
    if (!this.stepsEl) return;
    const names = this.allStepNames();
    const currentIndex = names.indexOf(this.currentStep);
    this.stepsEl.innerHTML = names
      .map((_, i) => {
        const cls =
          i < currentIndex ? 'is-done' : i === currentIndex ? 'is-active' : '';
        return `<li class="${cls}"></li>`;
      })
      .join('');
  }

  /* ----------------------------------------------------------------- */
  /*  Footer / navigation                                               */
  /* ----------------------------------------------------------------- */

  private renderFooter(): void {
    if (!this.footerEl) return;
    const step = this.currentStep;

    if (step === 'confirmation') {
      this.footerEl.innerHTML = `
        <button type="button" class="no4w-btn no4w-btn--primary" data-action="restart">
          Book another stay
        </button>
      `;
      this.footerEl
        .querySelector<HTMLButtonElement>('[data-action="restart"]')
        ?.addEventListener('click', () => this.restart());
      return;
    }

    let backLabel = 'Back';
    let primaryLabel = 'Next';
    let primaryDisabled = false;
    let primaryAction: 'next' | 'submit-enquiry' | 'submit-payment' = 'next';

    if (step === 'calendar') {
      primaryDisabled = !this.calendarValid;
      primaryAction = 'next';
    } else if (step === 'guest') {
      primaryDisabled = !this.guestValid;
      if (this.flow === 'enquiry') {
        primaryLabel = this.enquirySubmitting
          ? 'Sending…'
          : 'Send Enquiry';
        primaryAction = 'submit-enquiry';
        if (this.enquirySubmitting) primaryDisabled = true;
      } else {
        primaryLabel = 'Continue to Payment';
      }
    } else if (step === 'payment') {
      const submitting = this.payment?.isSubmitting() ?? false;
      primaryLabel = submitting
        ? 'Processing…'
        : `Pay & Confirm`;
      primaryAction = 'submit-payment';
      if (submitting) primaryDisabled = true;
    }

    const errBanner =
      step === 'guest' && this.flow === 'enquiry' && this.enquiryError
        ? `<div style="flex:1 0 100%;" role="status" aria-live="polite"><div class="no4w-banner no4w-banner--error">${escapeHTML(
            this.enquiryError,
          )}</div></div>`
        : '';

    const backButtonHtml =
      step === 'calendar'
        ? ''
        : `<button type="button" class="no4w-btn no4w-btn--ghost" data-action="back">
             ${escapeHTML(backLabel)}
           </button>`;

    this.footerEl.innerHTML = `
      ${errBanner}
      ${backButtonHtml}
      <button
        type="button"
        class="no4w-btn no4w-btn--primary"
        data-action="${primaryAction}"
        ${primaryDisabled ? 'disabled' : ''}
      >
        ${primaryAction === 'submit-payment' && this.payment?.isSubmitting()
          ? '<span class="no4w-loader"></span>'
          : ''}
        ${primaryAction === 'submit-enquiry' && this.enquirySubmitting
          ? '<span class="no4w-loader"></span>'
          : ''}
        ${escapeHTML(primaryLabel)}
      </button>
    `;

    this.footerEl
      .querySelector<HTMLButtonElement>('[data-action="back"]')
      ?.addEventListener('click', () => this.onBack());

    this.footerEl
      .querySelector<HTMLButtonElement>('[data-action="next"]')
      ?.addEventListener('click', () => this.onNext());

    this.footerEl
      .querySelector<HTMLButtonElement>('[data-action="submit-enquiry"]')
      ?.addEventListener('click', () => {
        void this.onSubmitEnquiry();
      });

    this.footerEl
      .querySelector<HTMLButtonElement>('[data-action="submit-payment"]')
      ?.addEventListener('click', () => {
        void this.payment?.submit();
        // re-render footer to reflect submitting state shortly after
        setTimeout(() => this.renderFooter(), 0);
      });
  }

  private onBack(): void {
    if (this.currentStep === 'calendar') return;
    const names = this.allStepNames();
    const idx = names.indexOf(this.currentStep);
    if (idx <= 0) return;
    this.currentStep = names[idx - 1];
    this.renderCurrentStep();
  }

  private onNext(): void {
    if (this.currentStep === 'calendar') {
      if (!this.calendarValid) return;
      this.currentStep = 'guest';
      this.renderCurrentStep();
      return;
    }
    if (this.currentStep === 'guest') {
      if (!this.guestValid) {
        this.guestForm?.showAllErrors();
        return;
      }
      if (this.flow === 'booking') {
        this.currentStep = 'payment';
        this.renderCurrentStep();
      } else {
        void this.onSubmitEnquiry();
      }
    }
  }

  private async onSubmitEnquiry(): Promise<void> {
    if (this.enquirySubmitting) return;
    if (!this.guestValid) {
      this.guestForm?.showAllErrors();
      return;
    }
    if (!this.calendarState.checkIn || !this.calendarState.checkOut) return;

    this.enquirySubmitting = true;
    this.enquiryError = null;
    this.renderFooter();

    try {
      const result = await this.api.createBooking({
        type: 'enquiry',
        check_in: toISODate(this.calendarState.checkIn),
        check_out: toISODate(this.calendarState.checkOut),
        num_adults: this.calendarState.numAdults,
        num_children: this.calendarState.numChildren,
        children_ages: this.calendarState.childAges,
        has_pets: this.calendarState.hasPets,
        first_name: this.guestState.firstName,
        last_name: this.guestState.lastName,
        email: this.guestState.email,
        phone: this.guestState.phone,
        notes: this.guestState.notes,
        gdpr_consent: this.guestState.gdprConsent,
      });
      this.bookingResult = result;
      this.enquirySubmitting = false;
      this.currentStep = 'confirmation';
      this.renderCurrentStep();
    } catch (e) {
      this.enquirySubmitting = false;
      this.enquiryError =
        e instanceof APIError
          ? e.message
          : 'Could not send enquiry. Please try again.';
      this.renderFooter();
    }
  }
}

/* ---------------------------------------------------------------------- */
/*  Balance-payment widget (data-mode="pay-balance")                       */
/*                                                                         */
/*  Standalone flow triggered by the balance-reminder email link:          */
/*  https://no4newquaywales.co.uk/pay/?ref=HT-2026-XXX&t=<pay_token>       */
/*                                                                         */
/*  Loads the balance PaymentIntent (auth = ref + token), mounts Stripe    */
/*  Elements, and processes the payment. No calendar/guest steps.          */
/* ---------------------------------------------------------------------- */

// deno-lint-ignore no-explicit-any
type StripeApi = any;
declare const window: Window & { Stripe?: (key: string) => StripeApi };

function loadStripeJs(): Promise<(key: string) => StripeApi> {
  return new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.async = true;
    s.onload = () => window.Stripe ? resolve(window.Stripe) : reject(new Error('Stripe.js failed to load'));
    s.onerror = () => reject(new Error('Stripe.js failed to load'));
    document.head.appendChild(s);
  });
}

class BalancePayWidget {
  private readonly root: HTMLElement;
  private readonly config: WidgetConfig;
  private readonly ref: string;
  private readonly token: string;
  private submitting = false;
  // deno-lint-ignore no-explicit-any
  private stripe: any = null;
  // deno-lint-ignore no-explicit-any
  private elements: any = null;
  // deno-lint-ignore no-explicit-any
  private paymentElement: any = null;
  private clientSecret: string | null = null;

  constructor(root: HTMLElement, config: WidgetConfig, ref: string, token: string) {
    this.root = root;
    this.config = config;
    this.ref = ref;
    this.token = token;
    this.root.classList.add('no4w-root');
    void this.init();
  }

  private async init(): Promise<void> {
    this.render('loading', 'Loading your booking…');
    try {
      // Request the balance PaymentIntent (ref+token auth).
      const res = await fetch(`${this.config.supabaseUrl}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.anonKey}`,
          'apikey': this.config.anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bookingRef: this.ref, token: this.token, type: 'balance' }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        this.render('error', payload?.error || 'Could not load your booking. Please contact us.');
        return;
      }
      this.clientSecret = payload.clientSecret;
      if (!this.clientSecret) {
        this.render('error', 'This booking has no outstanding balance.');
        return;
      }

      const StripeFn = await loadStripeJs();
      this.stripe = StripeFn(this.config.stripeKey);
      this.render('ready', '');
    } catch (e) {
      this.render('error', e instanceof Error ? e.message : 'Could not load your booking.');
    }
  }

  private render(state: 'loading' | 'ready' | 'error' | 'done', message: string): void {
    if (state === 'loading') {
      this.root.innerHTML = `<div class="no4w-inline"><p style="text-align:center;padding:24px;color:#666;">${escapeHTML(message)}</p></div>`;
      return;
    }
    if (state === 'error') {
      this.root.innerHTML = `<div class="no4w-inline"><div class="no4w-banner no4w-banner--error">${escapeHTML(message)}</div><p style="text-align:center;margin-top:16px;color:#666;font-size:13px;">If you need help, please email stay@no4newquaywales.co.uk.</p></div>`;
      return;
    }
    if (state === 'done') {
      this.root.innerHTML = `<div class="no4w-inline" style="text-align:center;padding:32px 24px;">
        <div style="font-size:48px;line-height:1;margin-bottom:16px;">✓</div>
        <h2 style="margin:0 0 8px 0;">Payment received</h2>
        <p style="color:#666;">Thank you — your balance has been paid in full. A confirmation email is on its way.</p>
      </div>`;
      return;
    }
    // ready
    this.root.innerHTML = `
      <div class="no4w-inline">
        <h2 class="no4w-modal-title">Pay your balance</h2>
        <p style="color:#666;font-size:14px;margin:0 0 20px 0;">
          Booking <strong style="font-family:monospace;">${escapeHTML(this.ref)}</strong> — please choose a payment method to pay the remaining balance.
        </p>
        <div id="no4w-bal-payment"></div>
        <div id="no4w-bal-error" role="alert" style="margin-top:12px;color:#b3261e;font-size:13px;"></div>
        <div style="margin-top:20px;text-align:center;">
          <button id="no4w-bal-pay" type="button" class="no4w-btn no4w-btn--primary">Pay balance</button>
        </div>
        <p style="text-align:center;margin-top:16px;color:#999;font-size:12px;">
          Payments are processed securely by Stripe. Your card details never touch our servers.
        </p>
      </div>
    `;
    // clientSecret is fetched during init(), so Payment Element can bind
    // directly to it (no deferred-PI mode needed here).
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      locale: 'en-GB',
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#1c2421',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSizeBase: '16px',
        },
      },
    });
    this.paymentElement = this.elements.create('payment', {
      layout: 'tabs',
    });
    this.paymentElement.mount('#no4w-bal-payment');
    this.root.querySelector<HTMLButtonElement>('#no4w-bal-pay')?.addEventListener('click', () => void this.pay());
  }

  private async pay(): Promise<void> {
    if (this.submitting || !this.clientSecret || !this.elements) return;
    const btn = this.root.querySelector<HTMLButtonElement>('#no4w-bal-pay');
    const err = this.root.querySelector<HTMLDivElement>('#no4w-bal-error');
    if (err) err.textContent = '';
    this.submitting = true;
    if (btn) { btn.disabled = true; btn.textContent = 'Processing…'; }
    try {
      // Payment Element must be locally validated before we call confirm.
      const submitRes = await this.elements.submit();
      if (submitRes?.error) {
        if (err) err.textContent = submitRes.error.message ?? 'Please complete your payment details.';
        this.submitting = false;
        if (btn) { btn.disabled = false; btn.textContent = 'Pay balance'; }
        return;
      }

      // redirect: 'if_required' → cards + wallets resolve inline; only
      // exotic methods (Klarna, iDEAL) would redirect to return_url.
      const confirm = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: this.clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (confirm.error) {
        if (err) err.textContent = confirm.error.message ?? 'Payment failed.';
        this.submitting = false;
        if (btn) { btn.disabled = false; btn.textContent = 'Pay balance'; }
        return;
      }
      const status = confirm.paymentIntent?.status;
      if (status === 'succeeded' || status === 'processing') {
        this.render('done', '');
        return;
      }
      if (err) err.textContent = 'Your payment needs additional verification. Please try again.';
      this.submitting = false;
      if (btn) { btn.disabled = false; btn.textContent = 'Pay balance'; }
    } catch (e) {
      if (err) err.textContent = e instanceof Error ? e.message : 'Payment failed.';
      this.submitting = false;
      if (btn) { btn.disabled = false; btn.textContent = 'Pay balance'; }
    }
  }
}

/* ---------------------------------------------------------------------- */
/*  Auto-mount + global API                                               */
/* ---------------------------------------------------------------------- */

function readConfig(el: HTMLElement): WidgetConfig | null {
  const supabaseUrl = el.dataset.supabaseUrl?.replace(/\s+/g, '');
  const anonKey = el.dataset.anonKey?.replace(/\s+/g, '');
  const stripeKey = el.dataset.stripeKey?.replace(/\s+/g, '');
  if (!supabaseUrl || !anonKey || !stripeKey) {
    // eslint-disable-next-line no-console
    console.error(
      '[No4Widget] Missing required data-* attributes: data-supabase-url, data-anon-key, data-stripe-key',
      el,
    );
    // Also surface the problem visibly, so a non-technical site owner
    // (e.g. Squarespace admin) can see the widget is misconfigured
    // rather than staring at a blank space.
    el.classList.add('no4w-root');
    el.innerHTML = `
      <div class="no4w-banner no4w-banner--error">
        Booking widget is misconfigured — please contact the property.
      </div>
    `;
    return null;
  }
  // Defensive: warn loudly if a secret key was pasted by mistake.
  if (!stripeKey.startsWith('pk_test_') && !stripeKey.startsWith('pk_live_')) {
    console.error('[No4Widget] data-stripe-key must be a publishable key (pk_test_… or pk_live_…). Refusing to mount.');
    el.classList.add('no4w-root');
    el.innerHTML = `<div class="no4w-banner no4w-banner--error">Booking widget is misconfigured (invalid Stripe key).</div>`;
    return null;
  }
  return {
    supabaseUrl,
    anonKey,
    stripeKey,
    privacyUrl: el.dataset.privacyUrl,
  };
}

const mountedRoots = new WeakSet<HTMLElement>();

function mount(el: HTMLElement, configOverride?: Partial<WidgetConfig>): void {
  if (mountedRoots.has(el)) return;
  const fromData = readConfig(el);
  if (!fromData && !configOverride) return;
  const config: WidgetConfig = {
    supabaseUrl: configOverride?.supabaseUrl ?? fromData!.supabaseUrl,
    anonKey: configOverride?.anonKey ?? fromData!.anonKey,
    stripeKey: configOverride?.stripeKey ?? fromData!.stripeKey,
    privacyUrl: configOverride?.privacyUrl ?? fromData?.privacyUrl,
  };
  mountedRoots.add(el);

  // Dispatch on data-mode. Default is the full booking flow.
  const mode = el.dataset.mode ?? 'booking';
  if (mode === 'pay-balance') {
    // Read ref + token from URL query string. Fall back to element data-*
    // attributes if the page owner wants to hardcode them for testing.
    const q = new URLSearchParams(window.location.search);
    const ref = q.get('ref') ?? el.dataset.ref ?? '';
    const token = q.get('t') ?? el.dataset.token ?? '';
    if (!ref || !token) {
      el.classList.add('no4w-root');
      el.innerHTML = `<div class="no4w-banner no4w-banner--error">This payment link is missing information. Please contact us using the link in your reminder email.</div>`;
      return;
    }
    new BalancePayWidget(el, config, ref, token);
    return;
  }
  new WidgetInstance(el, config);
}

function autoMount(): void {
  const seen = new Set<HTMLElement>();
  const canonical = document.getElementById('no4-booking-widget');
  if (canonical) seen.add(canonical);
  // Balance-pay page uses its own id so a site can host both flows.
  const payCanonical = document.getElementById('no4-pay-balance');
  if (payCanonical) {
    payCanonical.dataset.mode = 'pay-balance';
    seen.add(payCanonical);
  }
  document
    .querySelectorAll<HTMLElement>('[data-no4-widget]')
    .forEach((el) => seen.add(el));
  seen.forEach((el) => mount(el));
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount, { once: true });
  } else {
    autoMount();
  }
}

/** Public global, exposed via the IIFE bundle as `window.No4Widget`. */
export const No4Widget = {
  mount,
  version: '1.0.0',
};

if (typeof window !== 'undefined') {
  (window as unknown as { No4Widget: typeof No4Widget }).No4Widget = No4Widget;
}

export default No4Widget;
